<template>
  <div class="class-1">
    <!-- Header -->
    <AppHeader title="Edit Customer" />

    <!-- Customer Form -->
    <div class="container mt-4">
      <div class="row">
        <div class="col-12">
          <h2>Customer Details</h2>
          <form @submit.prevent="handleRegister">
            <!-- Basic Info -->
            <div class="form-row">
              <div class="form-group col-md-12">
                <label for="name">Name</label>
                <div class="searchbyphone d-flex flex-row">
                  <div class="infobox fieldOuter w-100">
                    <input
                      type="text"
                      class="form-control"
                      id="name"
                      placeholder="Name"
                      v-model="form.name"
                      ref="nameInput"
                      required
                    >
                  </div>
                  <span class="icon1 icon2" @click="openKeypad('nameInput')">
                    <i class="fas fa-keyboard"></i>
                  </span>
                </div>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group col-md-12">
                <label for="phone">Phone Number</label>
                <div class="searchbyphone d-flex flex-row">
                  <div class="infobox fieldOuter w-100">
                    <input
                      type="text"
                      class="form-control"
                      id="phone"
                      placeholder="Phone Number"
                      v-model="form.phone"
                      ref="phoneInput"
                      required
                    >
                  </div>
                  <span class="icon1 icon2" @click="openNumericKeypad('phoneInput')">
                    <i class="fas fa-keyboard"></i>
                  </span>
                </div>
              </div>
            </div>

            <!-- Credit Checkbox -->
            <div class="form-row">
              <div class="form-group col-md-12">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="credit"
                    v-model="form.credit"
                  >
                  <label class="form-check-label" for="credit">Credit Customer</label>
                </div>
              </div>
            </div>

            <!-- Balance Input (shown only if credit is true) -->
            <div class="form-row" v-if="form.credit">
              <div class="form-group col-md-12">
                <label for="balance">Balance</label>
                <div class="searchbyphone d-flex flex-row">
                  <div class="infobox fieldOuter w-100">
                    <input
                      type="text"
                      class="form-control"
                      id="balance"
                      placeholder="Balance"
                      v-model="form.balance"
                      ref="balanceInput"
                    >
                  </div>
                  <span class="icon1 icon2" @click="openNumericKeypad('balanceInput')">
                    <i class="fas fa-keyboard"></i>
                  </span>
                </div>
              </div>
            </div>

            <!-- Address Sections -->
            <template v-for="(address, index) in form.addresses" :key="index">
              <div class="address-section" v-if="index === 0 || showAddresses[index]">
                <h5 v-if="index === 0">Primary Address</h5>
                <h5 v-else>Additional Address {{ index + 1 }}</h5>
                
                <template v-if="index === 0 || editAddresses[index]">
                  <!-- Address Title -->
                  <div class="form-row">
                    <div class="form-group col-md-12">
                      <label :for="`title${index+1}`">Address Title</label>
                      <div class="searchbyphone d-flex flex-row">
                        <div class="infobox fieldOuter w-100">
                          <input
                            type="text"
                            class="form-control"
                            :id="`title${index+1}`"
                            placeholder="Address Title"
                            v-model="address.title"
                            :ref="el => { if (el) inputRefs[`title${index+1}`] = el }"
                          >
                        </div>
                        <span class="icon1 icon2" @click="openKeypad(`title${index+1}`)">
                          <i class="fas fa-keyboard"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Area -->
                  <div class="form-row">
                    <div class="form-group col-md-12">
                      <label :for="`area${index+1}`">Area</label>
                      <div class="searchbyphone d-flex flex-row">
                        <div class="infobox fieldOuter w-100">
                          <input
                            type="text"
                            class="form-control"
                            :id="`area${index+1}`"
                            placeholder="Area"
                            v-model="address.area"
                            :ref="el => { if (el) inputRefs[`area${index+1}`] = el }"
                          >
                        </div>
                        <span class="icon1 icon2" @click="openKeypad(`area${index+1}`)">
                          <i class="fas fa-keyboard"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Street -->
                  <div class="form-row">
                    <div class="form-group col-md-12">
                      <label :for="`street${index+1}`">Street</label>
                      <div class="searchbyphone d-flex flex-row">
                        <div class="infobox fieldOuter w-100">
                          <input
                            type="text"
                            class="form-control"
                            :id="`street${index+1}`"
                            placeholder="Street"
                            v-model="address.street"
                            :ref="el => { if (el) inputRefs[`street${index+1}`] = el }"
                          >
                        </div>
                        <span class="icon1 icon2" @click="openKeypad(`street${index+1}`)">
                          <i class="fas fa-keyboard"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Building -->
                  <div class="form-row">
                    <div class="form-group col-md-12">
                      <label :for="`build${index+1}`">Building</label>
                      <div class="searchbyphone d-flex flex-row">
                        <div class="infobox fieldOuter w-100">
                          <input
                            type="text"
                            class="form-control"
                            :id="`build${index+1}`"
                            placeholder="Building"
                            v-model="address.build"
                            :ref="el => { if (el) inputRefs[`build${index+1}`] = el }"
                          >
                        </div>
                        <span class="icon1 icon2" @click="openKeypad(`build${index+1}`)">
                          <i class="fas fa-keyboard"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Floor -->
                  <div class="form-row">
                    <div class="form-group col-md-12">
                      <label :for="`floor${index+1}`">Floor</label>
                      <div class="searchbyphone d-flex flex-row">
                        <div class="infobox fieldOuter w-100">
                          <input
                            type="text"
                            class="form-control"
                            :id="`floor${index+1}`"
                            placeholder="Floor"
                            v-model="address.floor"
                            :ref="el => { if (el) inputRefs[`floor${index+1}`] = el }"
                          >
                        </div>
                        <span class="icon1 icon2" @click="openKeypad(`floor${index+1}`)">
                          <i class="fas fa-keyboard"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Flat -->
                  <div class="form-row">
                    <div class="form-group col-md-12">
                      <label :for="`flat${index+1}`">Flat</label>
                      <div class="searchbyphone d-flex flex-row">
                        <div class="infobox fieldOuter w-100">
                          <input
                            type="text"
                            class="form-control"
                            :id="`flat${index+1}`"
                            placeholder="Flat"
                            v-model="address.flat"
                            :ref="el => { if (el) inputRefs[`flat${index+1}`] = el }"
                          >
                        </div>
                        <span class="icon1 icon2" @click="openKeypad(`flat${index+1}`)">
                          <i class="fas fa-keyboard"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="d-flex mt-3" v-if="index > 0">
                    <button @click="saveAddress(index)" class="btn btn-success mr-2">
                      <i class="fas fa-save"></i> Save
                    </button>
                    <button @click="removeAddress(index)" class="btn btn-danger">
                      <i class="fas fa-trash"></i> Remove
                    </button>
                  </div>
                </template>
                <template v-else>
                  <div class="address-display">
                    <p>{{ address.title }}</p>
                    <p>{{ address.area }} {{ address.street }} {{ address.build }}</p>
                    <p>Floor: {{ address.floor }}, Flat: {{ address.flat }}</p>
                  </div>
                  <button @click="editAddress(index)" class="btn btn-primary btn-sm mt-2">
                    <i class="fas fa-edit"></i> Edit
                  </button>
                </template>
              </div>
            </template>

            <div class="form-row" v-if="canAddAddress">
              <div class="form-group col-md-12 text-center">
                <button type="button" class="btn btn-outline-secondary" @click="addAddress">
                  + Add Another Address
                </button>
              </div>
            </div>

            <!-- Buttons -->
            <div class="form-row">
              <div class="form-group col-12 d-flex justify-content-between">
                <button type="button" class="btn btn-secondary" @click="handlecancel">
                  Cancel
                </button>
                <button type="submit" class="btn updatecat">
                  <i class="fas fa-save"></i> Update Customer
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

    <Keypad 
      ref="keypad"
      @update:value="updateInputValue"
      @cancel="closeKeypad"
    />
    <NumericKeypad 
      ref="numericKeypad"
      @update:value="updateNumericValue"
      @cancel="closeKeypad"
    />
  </div>
</template>

<script>
import NumericKeypad from './NumericKeypad.vue';
import Keypad from './keypad_popup_component.vue';
import { Modal } from 'bootstrap';

export default {
  components: {
    Keypad,
    NumericKeypad
  },
  data() {
    return {
      editAddresses: [false, false, false],
      showAddresses: [true, false, false],
      currentInputRef: null,
      currentInputName: null,
      keypadModal: null,
      numberModal: null,
      inputRefs: {}, // Object to store all dynamic input refs
      form: {
        name: '',
        phone: '',
        credit: false,
        balance: '',
        registration_date: null,
        addresses: [
          {
            title: 'Home',
            area: '',
            street: '',
            build: '',
            floor: '',
            flat: ''
          },
          {
            title: '',
            area: '',
            street: '',
            build: '',
            floor: '',
            flat: ''
          },
          {
            title: '',
            area: '',
            street: '',
            build: '',
            floor: '',
            flat: ''
          }
        ]
      },
      customerId: ''
    };
  },
  computed: {
    canAddAddress() {
      return !this.showAddresses[1] || !this.showAddresses[2];
    }
  },
  mounted() {
    this.customerId = this.$route.params.id;
    this.fetchUserData();
    
    this.$nextTick(() => {
      const keypadModalElement = this.$refs.keypad?.$el;
      if (keypadModalElement) {
        this.keypadModal = new Modal(keypadModalElement);
      }

      const numericModalElement = this.$refs.numericKeypad?.$el;
      if (numericModalElement) {
        this.numberModal = new Modal(numericModalElement);
      }
    });
  },
  methods: {
    editAddress(index) {
      this.editAddresses[index] = true;
      if (index > 0) {
        this.showAddresses[index] = true;
      }
    },
    saveAddress(index) {
      this.editAddresses[index] = false;
    },
    removeAddress(index) {
      if (confirm('Are you sure you want to remove this address?')) {
        this.form.addresses[index] = {
          title: '',
          area: '',
          street: '',
          build: '',
          floor: '',
          flat: ''
        };
        this.showAddresses[index] = false;
        this.editAddresses[index] = false;
      }
    },
    addAddress() {
      if (!this.showAddresses[1]) {
        this.showAddresses[1] = true;
        this.editAddresses[1] = true;
      } else if (!this.showAddresses[2]) {
        this.showAddresses[2] = true;
        this.editAddresses[2] = true;
      }
    },
    openKeypad(inputRef) {
      const input = this.inputRefs[inputRef] || this.$refs[inputRef];
      if (!input) {
        console.error(`Input ref ${inputRef} not found`);
        return;
      }

      this.currentInputRef = input;
      this.currentInputName = inputRef;
      
      this.$refs.keypad.setTargetInput(input);
      this.keypadModal.show();
    },
    openNumericKeypad(inputRef) {
      const input = this.inputRefs[inputRef] || this.$refs[inputRef];
      if (!input) {
        console.error(`Input ref ${inputRef} not found`);
        return;
      }

      this.currentInputRef = input;
      this.currentInputName = inputRef;
      
      this.$refs.numericKeypad.setTargetInput(input);
      this.numberModal.show();
    },
    updateInputValue(value) {
      if (this.currentInputName && this.currentInputRef) {
        this.currentInputRef.value = value;
        
        const path = this.getFormFieldPath(this.currentInputName);
        if (path) {
          const parts = path.split('.');
          let obj = this.form;
          
          for (let i = 0; i < parts.length - 1; i++) {
            obj = obj[parts[i]];
          }
          
          obj[parts[parts.length - 1]] = value;
        }
      }
    },
    updateNumericValue(value) {
      if (this.currentInputName === 'phoneInput') {
        this.form.phone = value;
      } else if (this.currentInputName === 'balanceInput') {
        this.form.balance = value;
      }
      this.closeKeypad();
    },
    getFormFieldPath(inputName) {
      const mapping = {
        nameInput: 'name',
        phoneInput: 'phone',
        balanceInput: 'balance',
        title1: 'addresses.0.title',
        area1: 'addresses.0.area',
        street1: 'addresses.0.street',
        build1: 'addresses.0.build',
        floor1: 'addresses.0.floor',
        flat1: 'addresses.0.flat',
        title2: 'addresses.1.title',
        area2: 'addresses.1.area',
        street2: 'addresses.1.street',
        build2: 'addresses.1.build',
        floor2: 'addresses.1.floor',
        flat2: 'addresses.1.flat',
        title3: 'addresses.2.title',
        area3: 'addresses.2.area',
        street3: 'addresses.2.street',
        build3: 'addresses.2.build',
        floor3: 'addresses.2.floor',
        flat3: 'addresses.2.flat'
      };
      
      return mapping[inputName];
    },
    closeKeypad() {
      if (this.keypadModal) {
        this.keypadModal.hide();
      }
      if (this.numberModal) {
        this.numberModal.hide();
      }
      this.removeBackdrop();
      this.currentInputRef = null;
      this.currentInputName = null;
    },
    removeBackdrop() {
      const backdrops = document.querySelectorAll('.modal-backdrop');
      backdrops.forEach(backdrop => backdrop.remove());
      document.body.classList.remove('modal-open');
      document.body.style.paddingRight = '';
    },
    handlecancel() {
      this.$router.push('/customers');
    },
    async fetchUserData() {
      try {
        this.$root.isLoading = true;
        const response = await fetch(`${this.$BASE_API_URL}/api/customers/${this.customerId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });
        const data = await response.json();
        
        this.form = {
          ...this.form,
          ...data.data,
          addresses: data.data.addresses || this.form.addresses
        };
        
        this.showAddresses = this.form.addresses.map((addr, index) => 
          index === 0 || addr.title || addr.area || addr.street || addr.build || addr.floor || addr.flat
        );
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        this.$root.isLoading = false;
      }
    },
    async handleRegister() {
      try {
        this.$root.isLoading = true;
        
        const addressesToSend = this.form.addresses.filter((addr, index) => {
          return index === 0 || addr.title || addr.area || addr.street || addr.build || addr.floor || addr.flat;
        });
        
        const payload = {
          name: this.form.name,
          phone: this.form.phone,
          credit: this.form.credit,
          balance: Number(this.form.balance) || 0,
          addresses: addressesToSend
        };
        
        const response = await fetch(`${this.$BASE_API_URL}/api/customers/${this.customerId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify(payload)
        });
        
        if (response.ok) {
          this.$toast.success('Customer updated successfully!');
          this.$router.push('/customers');
        } else {
          const errorData = await response.json();
          this.$toast.error('Update failed: ' + (errorData.message || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error updating customer:', error);
        this.$toast.error('An error occurred. Please try again.');
      } finally {
        this.$root.isLoading = false;
      }
    }
  }
};
</script>
<style scoped>

.updatecat {
  background-color: #4caf50;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  transition: all 0.3s;
}

.updatecat:hover {
  background-color: #3e8e41;
}

.address-section {
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #dee2e6;
}

.searchbyphone {
  position: relative;
  display: flex;
  align-items: stretch;
}

.infobox.fieldOuter {
  position: relative;
  width: 100%;
}

.icon1.icon2 {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 38px;
  border: 1px solid #ced4da;
  border-left: none;
  cursor: pointer;
  border-radius: 0 4px 4px 0;
  background-color: #f8f9fa;
  transition: all 0.2s;
}

.icon1.icon2:hover {
  background-color: #e9ecef;
}

.icon1.icon2 i {
  color: #495057;
  font-size: 16px;
}

.form-control {
  border-radius: 4px 0 0 4px !important;
  height: 38px;
}

.btn-outline-secondary {
  border: 1px dashed #6c757d;
  color: #6c757d;
  transition: all 0.3s;
  padding: 8px 16px;
}

.btn-outline-secondary:hover {
  border-style: solid;
  background-color: rgba(108, 117, 125, 0.1);
}

.address-display {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 15px;
  border-left: 4px solid #4caf50;
}

.address-display p {
  margin-bottom: 5px;
  color: #495057;
}

.address-display p:first-child {
  font-weight: 600;
  color: #212529;
}

.btn-primary, .btn-success, .btn-danger {
  padding: 6px 12px;
  font-size: 14px;
}

.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.form-row {
  margin-bottom: 15px;
}

.form-group {
  margin-bottom: 0;
}

.card-header {
  background-color: #f8f9fa;
  padding: 10px 15px;
}

.card-header h6 {
  margin-bottom: 0;
  font-weight: 600;
}

.d-flex.justify-content-between {
  margin-top: 20px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .container {
    padding: 15px;
  }
  
  .address-section {
    padding: 15px;
  }
  
  .icon1.icon2 {
    width: 35px;
  }
  
  .btn-outline-secondary, .updatecat, .btn-secondary {
    padding: 6px 12px;
    font-size: 14px;
  }
}


</style>